import { useEffect } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useAccrualReviewContext } from "./useAccrualReviewContext";

interface UseCellActionOpenerParams {
    table: { options: { meta?: object } };
    columnId: string;
    rowId: number;
    open: (() => void) | undefined;
}

/**
 * Lets the page trigger this cell's action through `openCellAction`. The
 * latest `open` is read at call time so registration doesn't churn; pass
 * `undefined` while the cell has nothing to open. Registers nothing unless
 * the provider sets `hasCellActionOpeners`.
 */
export const useCellActionOpener = ({
    table,
    columnId,
    rowId,
    open,
}: UseCellActionOpenerParams) => {
    const { registerCellActionOpener } = getTransactionsTableMeta(table);
    const { hasCellActionOpeners } = useAccrualReviewContext();
    const openRef = useLatestRef(open);

    useEffect(() => {
        if (!hasCellActionOpeners || !registerCellActionOpener) {
            return;
        }
        return registerCellActionOpener({
            columnId,
            rowId,
            open: () => openRef.current?.(),
        });
    }, [
        hasCellActionOpeners,
        registerCellActionOpener,
        columnId,
        rowId,
        openRef,
    ]);
};
